import React, { useState, useRef } from 'react';
import { AlertTriangle, AlertCircle, ArrowLeft, ArrowRight, X, ClipboardCheck, Upload, FileCheck } from 'lucide-react';
import { getStatusConstants, getAffectedTimestamps } from './utils/statusUtils';

/**
 * Status change confirmation modal
 * Displays warnings and collects additional input for status changes
 */
const StatusChangeConfirmation = ({
  direction,
  fromStatus,
  toStatus,
  onConfirm,
  onCancel,
  mode
}) => {
  // State for custom timestamp input
  const [useCustomTimestamp, setUseCustomTimestamp] = useState(false);
  const [customTimestamp, setCustomTimestamp] = useState(new Date());

  // State for conforming checkbox (for inbound putaway). Receiver notes are
  // captured via the multi-note thread (truck_notes) on the truck editor.
  const [conforming, setConforming] = useState(true);

  // State for BOL file upload
  const [bolFile, setBolFile] = useState(null);
  const [bolFileName, setBolFileName] = useState('');
  const fileInputRef = useRef(null);
  const [skipBolUpload, setSkipBolUpload] = useState(false);

  // Flag to determine if we're showing the putaway options - matching case with INBOUND_STATUS
  const isInboundPutaway = mode === 'inbound' && direction === 'forward' &&
    (fromStatus?.key === 'received' || fromStatus?.key === 'Received') &&
    (toStatus?.key === 'putaway' || toStatus?.key === 'Putaway');

  // Flag to determine if we're showing the BOL upload - only for outbound "Shipped" to "Completed"
  const isOutboundComplete = mode === 'outbound' && direction === 'forward' &&
    (fromStatus?.key === 'Shipped') &&
    (toStatus?.key === 'Completed');

  // Debug logging to help identify status key issues
  console.log(`StatusChangeConfirmation - mode: ${mode}, direction: ${direction}`);
  console.log(`StatusChangeConfirmation - fromStatus:`, fromStatus);
  console.log(`StatusChangeConfirmation - toStatus:`, toStatus);
  console.log(`StatusChangeConfirmation - isInboundPutaway: ${isInboundPutaway}`);
  console.log(`StatusChangeConfirmation - isOutboundComplete: ${isOutboundComplete}`);
  
  // Format timestamp for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    return new Date(date).toISOString().slice(0, 16);
  };
  
  // Determine warning level and message based on direction and statuses
  const getWarningDetails = () => {
    // Guard against undefined status objects
    if (!fromStatus || !toStatus || !fromStatus.key || !toStatus.key || !fromStatus.label || !toStatus.label) {
      // Provide default warning if status objects are incomplete
      return {
        level: 'high',
        icon: <AlertCircle className="w-5 h-5 text-red-500" />,
        message: 'Unable to determine status change details. This may cause unexpected changes to the truck status.',
        bgColor: 'bg-red-900/30',
        borderColor: 'border-red-800'
      };
    }

    const isForward = direction === 'forward';
    
    if (!isForward) {
      // Moving backward is always a high warning
      const modeSpecificMessage = mode === 'inbound'
        ? `This will revert the truck to '${toStatus.label}' status and clear all timestamps for subsequent statuses.`
        : `This will revert the truck to '${toStatus.label}' status. All later timestamps will be cleared, and the status field will be updated.`;
        
      return {
        level: 'high',
        icon: <AlertCircle className="w-5 h-5 text-red-500" />,
        message: `${modeSpecificMessage} This action cannot be undone.`,
        bgColor: 'bg-red-900/30',
        borderColor: 'border-red-800'
      };
    }
    
    // Moving forward has different warning levels
    if (fromStatus.key === 'scheduled' && toStatus.key === 'arrived') {
      return {
        level: 'low',
        icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
        message: `This will mark the truck as '${toStatus.label}' and set the arrival timestamp.`,
        bgColor: 'bg-yellow-900/30',
        borderColor: 'border-yellow-800'
      };
    }
    
    // Higher caution for key operational status changes
    if ((fromStatus.key === 'arrived' && toStatus.key === 'receiving') ||
        (fromStatus.key === 'receiving' && toStatus.key === 'completed') ||
        (fromStatus.key === 'staged' && toStatus.key === 'loading') ||
        (fromStatus.key === 'loading' && toStatus.key === 'loaded')) {
      return {
        level: 'medium',
        icon: <AlertTriangle className="w-5 h-5 text-orange-500" />,
        message: `This will update the truck's operational status to '${toStatus.label}' and set the corresponding timestamp.`,
        bgColor: 'bg-orange-900/30',
        borderColor: 'border-orange-800'
      };
    }
    
    // Default medium warning for other forward movements
    return {
      level: 'medium',
      icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
      message: `This will move the truck to the '${toStatus.label}' status and set the appropriate timestamp.`,
      bgColor: 'bg-yellow-900/30',
      borderColor: 'border-yellow-800'
    };
  };
  
  // Get affected timestamps for a backward movement
  const getAffectedTimestampsDisplay = () => {
    if (direction !== 'backward') return [];
    
    // Guard against undefined status objects
    if (!fromStatus || !toStatus || !fromStatus.key || !toStatus.key) {
      return ['Unknown timestamps'];
    }
    
    // Use the standardized utility function to determine affected fields
    const affectedFields = getAffectedTimestamps(fromStatus.key, toStatus.key, mode);
    
    // Map these technical field names to human-readable names
    const fieldNameMap = {
      // Inbound fields
      'scheduled_at': 'Scheduled Time',
      'arrival_datetime': 'Arrival Time',
      'started_at': 'Receiving Start Time',
      'completed_at': 'Completion Time',
      
      // Outbound fields
      'staging_start': 'Staging Start Time',
      'loading_start': 'Loading Start Time',
      'loading_complete': 'Loading Complete Time',
      'departure_datetime': 'Departure Time'
    };
    
    // Convert technical field names to human-readable names
    return affectedFields.map(field => fieldNameMap[field] || field);
  };
  
  const warning = getWarningDetails();
  const affectedTimestamps = getAffectedTimestampsDisplay();
  
  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBolFile(file);
      setBolFileName(file.name);
    }
  };

  // Trigger file input click
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Prepare additional data for specific status changes
    let additionalData = {};

    // For inbound putaway, include conforming status (notes are captured
    // separately via the multi-note thread on the truck editor)
    if (isInboundPutaway) {
      additionalData = {
        conforming
      };
    }

    // For outbound complete, include BOL file
    if (isOutboundComplete && bolFile) {
      // Create FormData and append the file in the API call
      // The actual file upload happens when the API is called
      additionalData = {
        ...additionalData,
        bolFile
      };
    }

    onConfirm(
      useCustomTimestamp ? customTimestamp : null,
      additionalData
    );
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg max-w-xl w-full shadow-xl">
        <div className="flex justify-between items-center p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-slate-200">
            Confirm Status Change
          </h3>
          <button 
            className="text-slate-400 hover:text-slate-200"
            onClick={onCancel}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-4">
            {/* Warning message */}
            <div className={`p-3 mb-4 rounded-lg flex items-start ${warning.bgColor} border ${warning.borderColor}`}>
              {warning.icon}
              <p className="ml-2 text-sm text-slate-200">
                {warning.message}
              </p>
            </div>
            
            {/* Status change visualization */}
            <div className="flex items-center justify-center mb-4 p-2">
              <div className="text-center">
                <div className="status-name font-medium text-slate-300">
                  {fromStatus.label}
                </div>
                <div className="text-xs text-slate-400">Current Status</div>
              </div>
              
              <div className="mx-4 flex items-center">
                {direction === 'forward' ? (
                  <ArrowRight className="w-6 h-6 text-blue-500" />
                ) : (
                  <ArrowLeft className="w-6 h-6 text-yellow-500" />
                )}
              </div>
              
              <div className="text-center">
                <div className="status-name font-medium text-slate-300">
                  {toStatus.label}
                </div>
                <div className="text-xs text-slate-400">New Status</div>
              </div>
            </div>
            
            {/* Affected timestamps warning */}
            {direction === 'backward' && affectedTimestamps.length > 0 && (
              <div className="mb-4 p-3 border rounded-lg border-red-800 bg-red-900/30">
                <h4 className="text-sm font-medium text-red-300">Data will be cleared:</h4>
                <ul className="mt-1 text-sm text-red-200">
                  {affectedTimestamps.map((timestamp, index) => (
                    <li key={index} className="flex items-center">
                      <span className="mr-1">•</span> {timestamp}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Timestamp controls */}
            <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700 mb-4">
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="useCustomTimestamp"
                  checked={useCustomTimestamp}
                  onChange={() => setUseCustomTimestamp(!useCustomTimestamp)}
                  className="mr-2"
                />
                <label htmlFor="useCustomTimestamp" className="text-sm text-slate-300">
                  Use custom timestamp
                </label>
              </div>

              {useCustomTimestamp && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1">
                    Custom Timestamp for {toStatus.label}:
                  </label>
                  <input
                    type="datetime-local"
                    value={formatDateForInput(customTimestamp)}
                    onChange={(e) => setCustomTimestamp(new Date(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-slate-200 text-sm"
                  />
                </div>
              )}

              {!useCustomTimestamp && (
                <p className="text-sm text-slate-400">
                  Current time will be used as the {toStatus.label} timestamp.
                </p>
              )}
            </div>

            {/* Inbound Putaway Options */}
            {isInboundPutaway && (
              <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700 mb-4">
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center">
                  <ClipboardCheck className="w-4 h-4 mr-2 text-blue-500" />
                  Putaway Options
                </h4>

                {/* Conforming checkbox */}
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="conforming"
                    checked={conforming}
                    onChange={() => setConforming(!conforming)}
                    className="mr-2"
                  />
                  <label htmlFor="conforming" className="text-sm text-slate-300">
                    Shipment conforms to requirements
                  </label>
                </div>

                <p className="text-xs text-slate-400 italic">
                  Add receiver notes from the truck editor's Notes section.
                </p>
              </div>
            )}

            {/* Outbound Complete BOL Upload */}
            {isOutboundComplete && (
              <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-slate-300 flex items-center">
                    <FileCheck className="w-4 h-4 mr-2 text-green-500" />
                    Please Upload Bill Of Lading (BOL)
                  </h4>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="skipBolUpload"
                      checked={skipBolUpload}
                      onChange={() => setSkipBolUpload(!skipBolUpload)}
                      className="mr-2"
                    />
                    <label htmlFor="skipBolUpload" className="text-xs text-amber-300">
                      Complete Without BOL
                    </label>
                  </div>
                </div>

                {!skipBolUpload && (
                  <>
                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff"
                      className="hidden"
                    />

                    {/* Upload UI */}
                    <div className="border-dashed border-2 border-slate-600 rounded-lg p-4 mb-3 text-center">
                      {bolFile ? (
                        <div className="flex flex-col items-center">
                          <FileCheck className="w-8 h-8 text-green-500 mb-2" />
                          <p className="text-sm text-slate-300 break-all">{bolFileName}</p>
                          <button
                            type="button"
                            onClick={handleBrowseClick}
                            className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                          >
                            Replace File
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Upload className="w-8 h-8 text-slate-500 mb-2" />
                          <p className="text-sm text-slate-400 mb-2">
                            Drag & drop your BOL file here, or
                          </p>
                          <button
                            type="button"
                            onClick={handleBrowseClick}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm text-white"
                          >
                            Browse Files
                          </button>
                          <p className="text-xs text-slate-500 mt-2">
                            Supported formats: PDF, Word, Images
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {skipBolUpload && (
                  <div className="p-3 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                    <p className="text-sm text-amber-200">
                      You've chosen to complete this truck without uploading a Bill of Lading document.
                      This can be uploaded later if needed.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="p-4 border-t border-slate-700 flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-sm"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md text-white text-sm ${
                warning.level === 'high'
                  ? 'bg-red-600 hover:bg-red-700'
                  : warning.level === 'medium'
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-blue-600 hover:bg-blue-700'
              } ${isOutboundComplete && !bolFile && !skipBolUpload ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isOutboundComplete && !bolFile && !skipBolUpload}
            >
              {isOutboundComplete && !bolFile && !skipBolUpload ? 'BOL Upload Required' : 'Confirm Status Change'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StatusChangeConfirmation;